/**
 * MediaItem Model - Gallery v2
 * 
 * Unified media model for both photos and videos in the gallery system.
 * Supports Firebase Storage, PhotonLink, and external video URLs.
 */

import { Timestamp } from 'firebase/firestore';

/**
 * Media type
 */
export type MediaType = 'photo' | 'video';

/**
 * Storage provider
 */
export type StorageProvider = 'firebase' | 'photonlink' | 'video-url';

/**
 * Video provider (for videos)
 */
export type VideoProvider = 'youtube' | 'vimeo' | 'google-drive' | 'other';

/**
 * Processing status for uploads
 */
export type ProcessingStatus = 'pending' | 'processing' | 'complete' | 'failed';

/**
 * Media visibility options
 */
export type MediaVisibility = 'public' | 'members' | 'officers' | 'private';

/**
 * Location data from EXIF or user input
 */
export interface MediaLocation {
  latitude: number;
  longitude: number;
  name?: string;                 // "SRAC Flying Field"
}

/**
 * Camera metadata from EXIF
 */
export interface CameraMetadata {
  make?: string;
  model?: string;
  lens?: string;
}

/**
 * Unified media item interface
 */
export interface MediaItem {
  id: string;                      // Firestore document ID
  
  // Basic Information
  title: string;                   // Display name
  description?: string;            // Caption or description
  type: MediaType;                 // 'photo' | 'video'
  
  // File Information
  url: string;                     // Full-size URL
  thumbnailUrl: string;            // Thumbnail URL (optimized)
  sourceUrl?: string;              // Original source if different
  
  // Dimensions
  width?: number;
  height?: number;
  aspectRatio?: number;            // width/height
  isPanorama?: boolean;            // true if equirectangular 360 panorama
  
  // Storage
  storageProvider: StorageProvider;
  storagePath?: string;            // Firebase Storage path (for photos)
  photonlinkMediaId?: string;      // If from PhotonLink

  // Deduplication
  crc?: string;                    // CRC32 checksum for duplicate detection
  
  // Album Association
  albumId: string;                 // Parent album
  albumPath: string;               // Denormalized for queries
  
  // EXIF & Metadata
  capturedAt?: Timestamp;          // When photo/video was taken
  uploadedAt: Timestamp;           // When uploaded to system
  location?: MediaLocation;
  camera?: CameraMetadata;
  tags: string[];                  // ["fixed-wing", "maiden-flight"]
  
  // Video-specific (videos are URL-based, validated for playability)
  videoUrl?: string;               // Full URL to embeddable video
  videoProvider?: VideoProvider;   // Detected from URL
  videoId?: string;                // Provider-specific video ID
  channelName?: string;            // YouTube/Vimeo channel
  channelUrl?: string;
  embedUrl?: string;               // Embed URL (generated from videoUrl)
  duration?: number;               // Duration in seconds
  
  // Ownership & Permissions
  uploadedBy: string;              // User ID
  uploaderName?: string;           // Denormalized display name
  visibility: MediaVisibility;
  
  // Interaction (simple metrics only)
  viewCount: number;
  
  // Processing
  processingStatus: ProcessingStatus;
  thumbnailGenerated: boolean;
  
  // Timestamps
  createdAt: Timestamp;
  updatedAt: Timestamp;

  // Aircraft Model Tagging
  aircraftModelIds?: string[];       // References to aircraftModels collection
  aircraftModelNames?: string[];     // Denormalized names for display
}

/**
 * Create a new MediaItem with default values for a photo
 */
export function createPhotoMediaItem(
  data: Partial<MediaItem> & Pick<MediaItem, 'title' | 'url' | 'thumbnailUrl' | 'albumId' | 'uploadedBy'>
): Omit<MediaItem, 'id'> {
  const now = Timestamp.now();
  
  return {
    title: data.title,
    description: data.description,
    type: 'photo',
    url: data.url,
    thumbnailUrl: data.thumbnailUrl,
    sourceUrl: data.sourceUrl,
    width: data.width,
    height: data.height,
    aspectRatio: data.width && data.height ? data.width / data.height : undefined,
    storageProvider: data.storageProvider || 'firebase',
    storagePath: data.storagePath,
    photonlinkMediaId: data.photonlinkMediaId,
    albumId: data.albumId,
    albumPath: data.albumPath || '',
    capturedAt: data.capturedAt,
    uploadedAt: data.uploadedAt || now,
    location: data.location,
    camera: data.camera,
    tags: data.tags || [],
    uploadedBy: data.uploadedBy,
    uploaderName: data.uploaderName,
    visibility: data.visibility || 'members',
    viewCount: data.viewCount || 0,
    processingStatus: data.processingStatus || 'pending',
    thumbnailGenerated: data.thumbnailGenerated || false,
    createdAt: data.createdAt || now,
    updatedAt: data.updatedAt || now,
  };
}

/**
 * Create a new MediaItem with default values for a video
 */
export function createVideoMediaItem(
  data: Partial<MediaItem> & Pick<MediaItem, 'title' | 'videoUrl' | 'albumId' | 'uploadedBy'>
): Omit<MediaItem, 'id'> {
  const now = Timestamp.now();
  const videoInfo = parseVideoUrl(data.videoUrl!);
  
  return {
    title: data.title,
    description: data.description,
    type: 'video',
    url: data.videoUrl!,
    thumbnailUrl: data.thumbnailUrl || videoInfo.thumbnailUrl || '',
    sourceUrl: data.videoUrl,
    width: data.width,
    height: data.height,
    aspectRatio: data.width && data.height ? data.width / data.height : 16/9,
    storageProvider: 'video-url',
    albumId: data.albumId,
    albumPath: data.albumPath || '',
    uploadedAt: data.uploadedAt || now,
    tags: data.tags || [],
    videoUrl: data.videoUrl,
    videoProvider: videoInfo.provider,
    videoId: videoInfo.videoId,
    channelName: data.channelName,
    channelUrl: data.channelUrl,
    embedUrl: videoInfo.embedUrl,
    duration: data.duration,
    uploadedBy: data.uploadedBy,
    uploaderName: data.uploaderName,
    visibility: data.visibility || 'members',
    viewCount: data.viewCount || 0,
    processingStatus: data.processingStatus || 'complete',
    thumbnailGenerated: data.thumbnailGenerated || !!videoInfo.thumbnailUrl,
    createdAt: data.createdAt || now,
    updatedAt: data.updatedAt || now,
  };
}

/**
 * Parse video URL to extract provider, video ID, embed URL, and thumbnail URL
 */
export function parseVideoUrl(url: string): {
  provider: VideoProvider;
  videoId: string | undefined;
  embedUrl: string;
  thumbnailUrl: string;
} {
  // YouTube patterns
  const youtubePatterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]+)/,
    /youtube\.com\/shorts\/([a-zA-Z0-9_-]+)/,
  ];
  
  for (const pattern of youtubePatterns) {
    const match = url.match(pattern);
    if (match) {
      const videoId = match[1];
      return {
        provider: 'youtube',
        videoId,
        embedUrl: `https://www.youtube.com/embed/${videoId}?autoplay=1&mute=1`,
        thumbnailUrl: `https://img.youtube.com/vi/${videoId}/0.jpg`,
      };
    }
  }
  
  // Vimeo pattern
  const vimeoPattern = /vimeo\.com\/(\d+)/;
  const vimeoMatch = url.match(vimeoPattern);
  if (vimeoMatch) {
    const videoId = vimeoMatch[1];
    return {
      provider: 'vimeo',
      videoId,
      embedUrl: `https://player.vimeo.com/video/${videoId}`,
      thumbnailUrl: '', // Vimeo requires API call for thumbnails
    };
  }
  
  // Google Drive patterns
  // Supports:
  // - https://drive.google.com/file/d/FILE_ID/view?usp=sharing
  // - https://drive.google.com/file/d/FILE_ID/preview
  // - https://drive.google.com/open?id=FILE_ID&usp=drive_fs
  // - https://drive.google.com/uc?id=FILE_ID
  // Note: Query parameters (?usp=...) are automatically ignored by the regex
  const drivePatterns = [
    /drive\.google\.com\/file\/d\/([a-zA-Z0-9_-]+)/,
    /drive\.google\.com\/open\?id=([a-zA-Z0-9_-]+)/,
    /drive\.google\.com\/uc\?(?:export=\w+&)?id=([a-zA-Z0-9_-]+)/,
  ];
  
  for (const pattern of drivePatterns) {
    const match = url.match(pattern);
    if (match) {
      const videoId = match[1];
      // Use /preview format as per Google's official embed code
      return {
        provider: 'google-drive',
        videoId,
        embedUrl: `https://drive.google.com/file/d/${videoId}/preview`,
        thumbnailUrl: `https://drive.google.com/thumbnail?id=${videoId}&sz=w640`,
      };
    }
  }
  
  // Default: treat as direct embed URL
  return {
    provider: 'other',
    videoId: undefined,
    embedUrl: url,
    thumbnailUrl: '',
  };
}

/**
 * Check if user can edit media item
 */
export function canEditMedia(
  media: MediaItem,
  userId: string,
  isOfficer: boolean,
  isAdmin: boolean
): boolean {
  // Admins and officers can edit any media
  if (isAdmin || isOfficer) {
    return true;
  }
  
  // Users can edit their own uploads
  if (media.uploadedBy === userId) {
    return true;
  }
  
  return false;
}

/**
 * Check if user can delete media item
 */
export function canDeleteMedia(
  media: MediaItem,
  userId: string,
  isOfficer: boolean,
  isAdmin: boolean
): boolean {
  // Admins and officers can delete any media
  if (isAdmin || isOfficer) {
    return true;
  }
  
  // Users can delete their own uploads
  if (media.uploadedBy === userId) {
    return true;
  }
  
  return false;
}

/**
 * Check if user can view media item
 */
export function canViewMedia(
  media: MediaItem,
  userId: string | null,
  isMember: boolean,
  isOfficer: boolean,
  isAdmin: boolean
): boolean {
  // Public media is visible to everyone
  if (media.visibility === 'public') {
    return true;
  }
  
  // Admins and officers can view all media
  if (isAdmin || isOfficer) {
    return true;
  }
  
  // Members can view member-only media
  if (media.visibility === 'members' && isMember) {
    return true;
  }
  
  // Officers can view officer-only media (already handled above)
  if (media.visibility === 'officers' && isOfficer) {
    return true;
  }
  
  // Users can view their own private media
  if (media.visibility === 'private' && media.uploadedBy === userId) {
    return true;
  }
  
  return false;
}

/**
 * Get the proper embed URL for a video, ensuring correct format
 * Reformats URLs if needed (e.g., Google Drive links, YouTube short links)
 * Always re-parses YouTube and Google Drive URLs to ensure correct embed format
 */
export function getVideoEmbedUrl(media: MediaItem): string {
  if (media.type !== 'video') {
    return media.url;
  }

  // Check URL to determine if it needs re-parsing
  const urlToCheck = media.videoUrl || media.url || '';
  const isGoogleDrive = media.videoProvider === 'google-drive' ||
                        urlToCheck.includes('drive.google.com');
  const isYouTube = media.videoProvider === 'youtube' ||
                    urlToCheck.includes('youtube.com') ||
                    urlToCheck.includes('youtu.be');

  // For Google Drive and YouTube, always re-parse to ensure correct embed format
  // (handles old database entries with wrong URLs or missing videoProvider field)
  if ((isGoogleDrive || isYouTube) && urlToCheck) {
    const videoInfo = parseVideoUrl(urlToCheck);
    return videoInfo.embedUrl;
  }

  // If we have a stored embedUrl, use it for other video providers
  if (media.embedUrl) {
    return media.embedUrl;
  }

  // If we have a videoUrl, parse it to get the correct embed URL
  if (media.videoUrl) {
    const videoInfo = parseVideoUrl(media.videoUrl);
    return videoInfo.embedUrl;
  }

  // Fallback to URL field
  return media.url;
}

/**
 * Get the proper thumbnail URL for a video
 * Always re-parses YouTube and Google Drive URLs to ensure correct thumbnail format
 */
export function getVideoThumbnailUrl(media: MediaItem): string {
  if (media.type !== 'video') {
    return media.thumbnailUrl;
  }

  // Check URL to determine if it needs re-parsing
  const urlToCheck = media.videoUrl || media.url || '';
  const isGoogleDrive = media.videoProvider === 'google-drive' ||
                        urlToCheck.includes('drive.google.com');
  const isYouTube = media.videoProvider === 'youtube' ||
                    urlToCheck.includes('youtube.com') ||
                    urlToCheck.includes('youtu.be');

  // For Google Drive videos, use our proxy API to fetch thumbnails
  // This ensures thumbnails work regardless of API key configuration
  if (isGoogleDrive) {
    // Use videoId if available (already extracted), otherwise extract from URL
    const fileId = media.videoId || extractDriveFileIdFromUrl(urlToCheck);
    if (fileId) {
      return `/api/google-drive/thumbnail?fileId=${fileId}`;
    }
  }

  // For YouTube, re-parse to ensure correct thumbnail format
  if (isYouTube && urlToCheck) {
    const videoInfo = parseVideoUrl(urlToCheck);
    return videoInfo.thumbnailUrl || '';
  }

  // If we have a stored thumbnail, use it for other video providers
  if (media.thumbnailUrl) {
    return media.thumbnailUrl;
  }

  // Try to generate from video URL
  if (media.videoUrl) {
    const videoInfo = parseVideoUrl(media.videoUrl);
    return videoInfo.thumbnailUrl || '';
  }

  return '';
}

/**
 * Helper function to extract Drive file ID from URL
 */
function extractDriveFileIdFromUrl(url: string): string | null {
  const patterns = [
    /drive\.google\.com\/file\/d\/([a-zA-Z0-9_-]+)/,
    /drive\.google\.com\/open\?id=([a-zA-Z0-9_-]+)/,
    /drive\.google\.com\/uc\?(?:export=\w+&)?id=([a-zA-Z0-9_-]+)/,
  ];

  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match) return match[1];
  }

  return null;
}

/**
 * Generate thumbnail URL for PhotonLink media
 */
export function getPhotonLinkThumbnail(media: MediaItem): string {
  if (media.storageProvider === 'photonlink' && media.photonlinkMediaId) {
    return media.thumbnailUrl;
  }
  return media.url;
}

/**
 * Get display dimensions for media item
 */
export function getDisplayDimensions(
  media: MediaItem,
  maxWidth: number = 1200,
  maxHeight: number = 800
): { width: number; height: number } {
  if (!media.width || !media.height) {
    return { width: maxWidth, height: maxHeight };
  }
  
  const aspectRatio = media.width / media.height;
  let width = media.width;
  let height = media.height;
  
  if (width > maxWidth) {
    width = maxWidth;
    height = width / aspectRatio;
  }
  
  if (height > maxHeight) {
    height = maxHeight;
    width = height * aspectRatio;
  }
  
  return { width: Math.round(width), height: Math.round(height) };
}
